import { observer } from 'mobx-react';
import isNil from 'ramda/src/isNil';
import React, { ReactNode } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { isNilOrEmpty } from '~common/helper/global';
import { DataError } from '~common/components/core/data-error.component';
import { Dialog } from '~web/view/components/layout/dialog.component';
import { ModalProps } from '~web/view/components/layout/modal.component';
import { Box } from '~common/components/box.component';
import { Column } from '~common/components/column.component';
import { BackButton } from '~web/view/components/ui/button/back-button.component';
import { CloseIcon } from '~web/view/components/ui/icon/close-icon.component';
import { Span } from '~common/components/span.component';
import { onTabletMediaQuery, ScrollBar } from '~web/view/theme/utils';

export interface OverlayProps extends ModalProps {
  title?: string;
  noCloseButton?: boolean;
  onBack?: () => void; // if present, the back button will show
  error?: Error;
  errorChildren?: ReactNode;
  onErrorClose?: VoidFunction;
}

//TODO: Move this component to common
export const Overlay: React.FunctionComponent<OverlayProps> = observer(
  ({
    backdropColor,
    onCancel,
    title,
    noCloseButton,
    onBack,
    disableBackdropClick,
    error,
    errorChildren,
    onErrorClose,
    children,
    ...rest
  }) => {
    const showCloseButton = !noCloseButton;
    const showBackButton = !isNil(onBack);
    const showHeader = title || !noCloseButton || showBackButton;

    return (
      <StyledDialog
        disableBackdropClick={disableBackdropClick}
        backdropColor={backdropColor}
        onCancel={onCancel}
        {...rest}
      >
        {showHeader && (
          <Header
            hasTitle={!isNilOrEmpty(title)}
            hasCloseButton={showCloseButton}
            hasBackButton={showBackButton}
            className={'overlay-header'}
          >
            {showBackButton && <BackButton onBack={onBack} />}
            {!isNilOrEmpty(title) && <Title>{title}</Title>}
            {showCloseButton && (
              <OverlayCloseIcon
                onClick={(): void => {
                  // onCancel is () => void, using it directly results in MouseEvent being
                  // passed back as a parameter and can be accidentally used inside a handler
                  onCancel();
                }}
              />
            )}
          </Header>
        )}
        <ScrollableContainer className={'overlay-scrollable-content'}>
          <Container className={'overlay-content'}>{children}</Container>
        </ScrollableContainer>
        <PositionAbsoluteDataError error={error} onClose={onErrorClose}>
          {errorChildren}
        </PositionAbsoluteDataError>
      </StyledDialog>
    );
  },
);

const StyledDialog = styled(Dialog)`
  && {
    background: ${(props): string => props.theme.colors.black};
    padding: 24px 0;
    width: 100%;
    min-width: 100%;
    min-height: 100%;
    border: none;
    border-radius: 0;
    margin: 0;

    ${onTabletMediaQuery} {
      border: 1px solid ${(props): string => props.theme.colors.gray.v3};
      padding: 40px 0;
      min-width: 500px;
      min-height: unset;
      max-height: 95%;
      border-radius: 16px;
    }
  }
`;

const ScrollableContainer = styled(Column)`
  width: 100%;
  flex: 1;
  ${ScrollBar};
`;

const Container = styled(Column)`
  width: 100%;
  flex: 1;
  padding: 0 24px;

  ${onTabletMediaQuery} {
    padding: 0 40px;
  }
`;

const Header = styled(({ hasTitle, hasCloseButton, hasBackButton, ...rest }) => <Box {...rest} />)<{
  hasTitle?: boolean;
  hasCloseButton?: boolean;
  hasBackButton?: boolean;
}>`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  ${(props): FlattenSimpleInterpolation | null =>
    props.hasCloseButton &&
    !(props.hasTitle || props.hasBackButton) &&
    css`
      justify-content: flex-end;
    `};

  padding: 0 24px;

  ${onTabletMediaQuery} {
    padding: 0 40px;
  }
`;

const Title = styled(Span)`
  font-weight: bold;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.8px;
  color: ${(props): string => props.theme.colors.white};
`;

const OverlayCloseIcon = styled(CloseIcon)`
  z-index: ${(props): number => props.theme.zIndex.over};
`;

const PositionAbsoluteDataError = styled(DataError)`
  position: absolute;
  ${onTabletMediaQuery} {
    border-radius: 0 0 16px 16px;
  }
`;



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/web/src/js/view/components/layout/overlay.component.tsx